import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit, Trash2, Users, Lock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * User Types Management — consolidated Role model.
 * Backed directly by `/api/rbac/roles`. Editing the page_access map here
 * updates the same underlying role that Permission Matrix and the Users page
 * consume, so all three views always agree.
 */

const PAGE_KEYS = [
  { key: 'dashboard', label: 'Dashboard', group: 'core' },
  { key: 'my_day', label: 'My Day', group: 'core' },
  { key: 'kanban_board', label: 'Kanban Board', group: 'core' },
  { key: 'task_table', label: 'Task Table', group: 'core' },
  { key: 'developer_queue', label: 'Developer Queue', group: 'core' },
  { key: 'uiux_queue', label: 'UI/UX Queue', group: 'core' },
  { key: 'manager_approval', label: 'Manager Approval', group: 'special' },
  { key: 'social_media', label: 'Social Media', group: 'special' },
  { key: 'reports', label: 'Reports', group: 'special' },
  { key: 'activity_log', label: 'Activity Log', group: 'admin' },
  { key: 'users', label: 'Users Management', group: 'admin' },
  { key: 'user_types', label: 'User Types', group: 'admin' },
  { key: 'settings', label: 'Settings', group: 'admin' },
];

const emptyAccess = () => PAGE_KEYS.reduce((acc, p) => ({ ...acc, [p.key]: false }), {});

const UserTypesManagement = () => {
  const { token } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    display_name: '',
    description: '',
    page_access: emptyAccess()
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const response = await axios.get(`${API}/rbac/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = (response.data?.roles || response.data || []).filter(r => r.is_active !== false);
      list.sort((a, b) => (a.hierarchy_level || 99) - (b.hierarchy_level || 99));
      setRoles(list);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(
          `${API}/rbac/roles/${editingRole.id}`,
          {
            display_name: formData.display_name,
            description: formData.description,
            page_access: formData.page_access,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Role updated!');
      } else {
        const slug = formData.display_name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        await axios.post(
          `${API}/rbac/roles`,
          {
            name: slug,
            display_name: formData.display_name,
            description: formData.description,
            hierarchy_level: 5,
            is_system_role: false,
            page_access: formData.page_access,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Role created!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadRoles();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save role';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save role');
    }
  };

  const handleDelete = async (roleId, isSystem) => {
    if (isSystem) {
      toast.error('System roles cannot be deleted');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await axios.delete(`${API}/rbac/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted!');
      loadRoles();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete role';
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete role');
    }
  };

  const resetForm = () => {
    setFormData({ display_name: '', description: '', page_access: emptyAccess() });
    setEditingRole(null);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setFormData({
      display_name: role.display_name || role.name,
      description: role.description || '',
      page_access: { ...emptyAccess(), ...(role.page_access || {}) }
    });
    setIsDialogOpen(true);
  };

  const togglePermission = (key) => {
    setFormData({
      ...formData,
      page_access: { ...formData.page_access, [key]: !formData.page_access[key] }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-types-page">
      <div className="flex items-center justify-between">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-3">
            <Users className="text-cyan-500" size={32} />
            User Types Management
          </h1>
          <p className="page-subtitle">Edit each role&apos;s page access. Granular permissions are edited in Permission Matrix.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-user-type-button" className="btn-cta">
              <Plus size={18} className="mr-2" /> Add Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRole ? `Edit "${editingRole.display_name || editingRole.name}"` : 'Create Role'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label>Role Name</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="e.g., Content Writer, SEO Analyst"
                  required
                  data-testid="role-name-input"
                  disabled={editingRole?.is_system_role}
                />
                {editingRole?.is_system_role && (
                  <p className="text-xs text-amber-600">System role — name is fixed, page access is editable.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role responsibilities..."
                  rows={3}
                  data-testid="role-description-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="text-cyan-600" size={20} />
                  <Label className="text-base font-semibold">Page Access</Label>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Select which pages this role can access. Turning a page ON also grants the
                  matching granular permissions in the Permission Matrix (view / create / edit).
                </p>

                <div className="grid grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg">
                  {['core', 'special', 'admin'].map(group => (
                    <div key={group} className="space-y-3">
                      <p className="text-sm font-semibold text-slate-700 mb-2 capitalize">
                        {group === 'core' ? 'Core Features' : group === 'special' ? 'Special Access' : 'Admin Only'}
                      </p>
                      {PAGE_KEYS.filter(p => p.group === group).map(p => (
                        <div key={p.key} className="flex items-center space-x-2">
                          <Checkbox
                            id={`perm-${p.key}`}
                            checked={!!formData.page_access[p.key]}
                            onCheckedChange={() => togglePermission(p.key)}
                            data-testid={`permission-${p.key.replace(/_/g, '-')}`}
                          />
                          <label htmlFor={`perm-${p.key}`} className="text-sm cursor-pointer">
                            {p.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                  <p className="text-xs text-amber-800">
                    <strong>Note:</strong> The <em>admin</em> role always has full access, regardless of page-access settings.
                  </p>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-user-type-button">Save Role</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div
            key={role.id}
            data-testid={`user-type-card-${role.id}`}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
                  <Users className="text-cyan-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{role.display_name || role.name}</h3>
                  {role.is_system_role && (
                    <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wide">System role</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEditDialog(role)}
                  data-testid={`edit-user-type-${role.id}`}
                >
                  <Edit size={16} />
                </Button>
                {!role.is_system_role && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(role.id, role.is_system_role)}
                    data-testid={`delete-user-type-${role.id}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </div>

            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              {role.description || 'No description provided'}
            </p>

            {role.page_access && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={14} className="text-slate-500" />
                  <p className="text-xs font-semibold text-slate-700">Page Access:</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(role.page_access)
                    .filter(([_, v]) => v)
                    .map(([key]) => (
                      <span
                        key={key}
                        className="text-xs px-2 py-1 bg-cyan-100 text-cyan-700 rounded-full"
                      >
                        {key.replace(/_/g, ' ')}
                      </span>
                    ))}
                  {Object.values(role.page_access).every(v => !v) && (
                    <span className="text-xs text-slate-500 italic">No page access</span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {roles.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <Users className="mx-auto text-slate-300 mb-4" size={64} />
          <h3 className="text-xl font-bold text-slate-800 mb-2">No Roles Yet</h3>
          <p className="text-slate-600 mb-4">Create your first role to organize your team</p>
        </div>
      )}
    </div>
  );
};

export default UserTypesManagement;
